import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension' // required for devtools typing
import type {
  Bid,
  BidStatus,
  BidPriority,
  BidScopeProfile,
  BidRoute,
  CreateBid,
  UpdateBid,
  User,
} from '@/lib/schemas'
import { api } from '@/lib/api'
import { countActiveBids, deadlineFilterBound } from '@/lib/bid-deadline'
import {
  bidLifecycleOf,
  withStatus,
  type BidLifecycle,
  type BidStage,
  type LifecycleCounts,
} from '@/lib/bid-lifecycle'

// Module-scope so it survives component remounts — React state (and anything
// in the store) is reset by StrictMode's mount/unmount/mount, which is exactly
// when the duplicate requests were being issued. Guards `ensureAllBids` only;
// `fetchAllBids` stays an unconditional refresh.
//
// Because it outlives the store, it MUST be cleared by reset() on sign-out:
// otherwise a sign-out/sign-in inside the request window would have user B
// await user A's promise and adopt A's tenant bid list.
let inFlightAllBids: Promise<void> | null = null

// "Loaded" is not "non-empty" — a brand-new org legitimately has zero bids, and
// without this sentinel ensureAllBids would re-request the full list on every
// Home / My Work mount forever.
let allBidsLoaded = false

// Generation counter: bumped by reset() so a response already in flight when the
// user signed out cannot write another tenant's bids into the fresh store.
let allBidsRequestId = 0

// The same generation guard for the single-bid fetch. Bumped by every
// fetchBidById and by reset(), so only the most recent request may write
// `selectedBid` — a slower fetch for a bid the user has already left cannot
// land on top of the bid they are looking at now.
let selectedBidRequestId = 0

/** A bid's deadline as the refresh compares it: the value and its time flag. */
interface DeadlineSnapshot {
  deadline?: string | null | undefined
  deadlineHasTime?: boolean | null | undefined
}

// The API sends `deadline: null` for "none"; the Bid type models it as
// optional. A flag of null (not recorded) and false both mean date-only.
function sameDeadline(a: DeadlineSnapshot | undefined, b: DeadlineSnapshot | undefined): boolean {
  return (
    (a?.deadline ?? null) === (b?.deadline ?? null) &&
    (a?.deadlineHasTime === true) === (b?.deadlineHasTime === true)
  )
}

interface FetchBidsParams {
  page?: number
  pageSize?: number
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

interface PaginatedResult<T> {
  items: T[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
  /** R4: per-lifecycle counts for the current filters except lifecycle. Absent before T2. */
  lifecycleCounts?: LifecycleCounts
}

/** A lifecycle move through PATCH /bids/{id} (plan §2.7). Never carries `status`. */
export type LifecycleMove =
  | { lifecycle: 'lead' }
  | { lifecycle: 'active'; stage: BidStage }

interface BidsFilter {
  search?: string | undefined
  statuses: BidStatus[]
  /** R4: the Bids tab. Sent as `lifecycle=`, and applied client-side too. */
  lifecycles: BidLifecycle[]
  /** R4: the stage filter. Sent as `stage=`, and applied client-side too. */
  stages: BidStage[]
  priorities: BidPriority[]
  clients: string[]
  deadlineFrom?: string | undefined
  deadlineTo?: string | undefined
  valueMin?: number | undefined
  valueMax?: number | undefined
}

interface Pagination {
  page: number
  pageSize: number
  total: number
  hasMore: boolean
}

interface BidsState {
  // State
  bids: Bid[]
  deletedBids: Bid[]
  selectedBid: Bid | null
  isLoadingBids: boolean
  error: string | null
  filter: BidsFilter
  pagination: Pagination
  sortBy: string
  sortOrder: 'asc' | 'desc'
  users: User[]
  _lastFetchId: number
  /**
   * What `bids` currently holds: the full org list (`fetchAllBids`) or one
   * filtered/paginated table page (`fetchBids`). Both write the same array.
   */
  bidsScope: 'all' | 'page' | null
  /**
   * Active, non-archived bid count from the last full-list load. The sidebar
   * shows this while `bids` holds a table page, so its count doesn't jump
   * when the Bids table is filtered or paginated.
   */
  activeBidCount: number | null
  /**
   * Per-lifecycle counts from the last Bids table fetch, for the tab labels.
   * Null while the backend does not send them (before R4's T2).
   */
  lifecycleCounts: LifecycleCounts | null

  // Computed (backward compat)
  isLoading: boolean

  // Actions
  fetchBids: (params?: FetchBidsParams) => Promise<void>
  fetchAllBids: () => Promise<void>
  ensureAllBids: () => Promise<void>
  reset: () => void
  fetchBidById: (id: string) => Promise<void>
  /**
   * Silently pick up a deadline the server set or moved after a key-dates run
   * (extraction copies the tender close onto the bid unless a person owns the
   * deadline). Best-effort: no loading state, no error surfaced.
   */
  refreshBidDeadline: (id: string) => Promise<void>
  createBid: (bid: CreateBid) => Promise<Bid>
  updateBid: (bid: UpdateBid) => Promise<void>
  deleteBid: (id: string) => Promise<void>
  updateBidStatus: (id: string, status: BidStatus) => Promise<void>
  moveBid: (id: string, newStatus: BidStatus) => void
  /** R4: move an active bid to another stage. Throws the API error (400/409). */
  moveBidStage: (id: string, stage: BidStage) => Promise<void>
  /** R4: a lifecycle move (to Leads, start bidding, reopen). Throws the API error (400/409). */
  moveBidLifecycle: (id: string, move: LifecycleMove) => Promise<void>
  /** Quietly re-read one bid and replace it wherever it is held. Best-effort. */
  refreshBid: (id: string) => Promise<void>
  selectBid: (bid: Bid | null) => void
  setFilter: (filter: Partial<BidsFilter>) => void
  clearError: () => void
  duplicateBid: (id: string) => Promise<Bid>
  archiveBid: (id: string) => Promise<void>
  unarchiveBid: (id: string) => Promise<void>
  bulkUpdateStatus: (ids: string[], status: BidStatus) => Promise<void>
  bulkDelete: (ids: string[]) => Promise<void>
  setBidVisibility: (id: string, visibility: 'org' | 'restricted') => Promise<void>
  setBidScope: (id: string, patch: { scopeProfile?: BidScopeProfile; bidRoute?: BidRoute }) => Promise<void>
  fetchDeletedBids: () => Promise<void>
  restoreBid: (id: string) => Promise<void>
  fetchUsers: () => Promise<void>

  // Computed
  getFilteredBids: () => Bid[]
}

/** Replace one bid (as the server returned it) in the list and the selection. */
function replaceBid(state: BidsState, updated: Bid): Partial<BidsState> {
  return {
    bids: state.bids.map((b) => (b.id === updated.id ? updated : b)),
    selectedBid: state.selectedBid?.id === updated.id ? updated : state.selectedBid,
  }
}

/**
 * A status set through the old status route (harvest, the board). The new
 * fields are re-derived so the header and tabs agree with it at once.
 */
function patchBidStatus(state: BidsState, id: string, status: BidStatus): Partial<BidsState> {
  return {
    bids: state.bids.map((b) => (b.id === id ? withStatus(b, status) : b)),
    selectedBid:
      state.selectedBid?.id === id ? withStatus(state.selectedBid, status) : state.selectedBid,
  }
}

export const useBidsStore = create<BidsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      bids: [],
      deletedBids: [],
      selectedBid: null,
      isLoadingBids: false,
      error: null,
      filter: {
        statuses: [],
        lifecycles: [],
        stages: [],
        priorities: [],
        clients: [],
      },
      pagination: {
        page: 1,
        pageSize: 25,
        total: 0,
        hasMore: false,
      },
      sortBy: 'createdAt',
      sortOrder: 'desc',
      users: [],
      _lastFetchId: 0,
      bidsScope: null,
      activeBidCount: null,
      lifecycleCounts: null,

      // Backward compat computed property
      get isLoading() {
        return get().isLoadingBids
      },

      // Fetch paginated bids
      fetchBids: async (params?: FetchBidsParams) => {
        const fetchId = get()._lastFetchId + 1
        set({ _lastFetchId: fetchId })

        try {
          set({ isLoadingBids: true, error: null })

          const { filter, pagination, sortBy, sortOrder } = get()

          const queryParams: Record<string, string | number | undefined> = {
            page: params?.page ?? pagination.page,
            pageSize: params?.pageSize ?? pagination.pageSize,
            search: filter.search || undefined,
            status: filter.statuses?.length ? filter.statuses.join(',') : undefined,
            lifecycle: filter.lifecycles?.length ? filter.lifecycles.join(',') : undefined,
            stage: filter.stages?.length ? filter.stages.join(',') : undefined,
            priority: filter.priorities?.length ? filter.priorities.join(',') : undefined,
            // The filter's dates bound the viewer's own days, closing times included.
            deadlineFrom: filter.deadlineFrom ? deadlineFilterBound(filter.deadlineFrom, 'from') : undefined,
            deadlineTo: filter.deadlineTo ? deadlineFilterBound(filter.deadlineTo, 'to') : undefined,
            valueMin: filter.valueMin,
            valueMax: filter.valueMax,
            sortBy: params?.sortBy ?? sortBy,
            sortOrder: params?.sortOrder ?? sortOrder,
          }

          const result = await api.get<PaginatedResult<Bid>>('/bids', queryParams)

          // Stale request guard
          if (get()._lastFetchId !== fetchId) {
            return // Ignore stale response
          }

          set({
            bids: result.items,
            bidsScope: 'page',
            lifecycleCounts: result.lifecycleCounts ?? null,
            pagination: {
              page: result.page,
              pageSize: result.pageSize,
              total: result.total,
              hasMore: result.hasMore,
            },
            isLoadingBids: false,
          })
        } catch (error) {
          // Only update error if this is still the latest request
          if (get()._lastFetchId === fetchId) {
            set({
              error: error instanceof Error ? error.message : 'Failed to fetch bids',
              isLoadingBids: false,
            })
          }
        }
      },

      // Fetch all bids (for Kanban) — always hits the network.
      fetchAllBids: async () => {
        const requestId = ++allBidsRequestId
        const pageFetchIdAtStart = get()._lastFetchId
        try {
          set({ isLoadingBids: true, error: null })
          const result = await api.get<PaginatedResult<Bid>>('/bids', { pageSize: 500 })
          // Orphaned by a reset (sign-out) or a newer request — drop it rather
          // than writing a stale tenant's bids into the current store.
          if (requestId !== allBidsRequestId) return
          const activeBidCount = countActiveBids(result.items)
          // A paginated table fetch started while this one was in flight (the
          // sidebar loads the full list on every page, including a deep link to
          // /app/bids). The table's request is the newer intent — overwriting
          // `bids` with the full list would break its pagination. Keep the count.
          if (get()._lastFetchId !== pageFetchIdAtStart) {
            set({ activeBidCount, isLoadingBids: false })
            return
          }
          allBidsLoaded = true
          set({ bids: result.items, bidsScope: 'all', activeBidCount, isLoadingBids: false })
        } catch (error) {
          if (requestId !== allBidsRequestId) return
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch bids',
            isLoadingBids: false,
          })
        }
      },

      // Load the bid list if we don't have it, sharing one request between
      // concurrent callers (F8). Callers used to guard with `bids.length === 0`
      // themselves, which forced `bids.length` into their effect dep arrays and
      // re-fired every sibling fetch the moment the list arrived.
      ensureAllBids: async () => {
        if (allBidsLoaded || get().bids.length > 0) return
        if (inFlightAllBids) return inFlightAllBids

        const request: Promise<void> = get()
          .fetchAllBids()
          .finally(() => {
            // Only retract our own promise. After a sign-out mid-request, user
            // B may already have stored theirs — an unguarded null here would
            // drop it and cost B a duplicate request.
            if (inFlightAllBids === request) inFlightAllBids = null
          })
        inFlightAllBids = request
        return request
      },

      // Clears the de-duplication state that lives outside Zustand, so
      // setState(getInitialState()) on sign-out actually leaves a clean store.
      reset: () => {
        allBidsRequestId++
        selectedBidRequestId++
        inFlightAllBids = null
        allBidsLoaded = false
      },

      // Fetch single bid by ID
      fetchBidById: async (id: string) => {
        const requestId = ++selectedBidRequestId
        try {
          // Clearing `selectedBid` when the id changes is what makes
          // BidDetailsPage's `isLoading && !bid` guard fire. Without it the
          // previous bid's title, status, metadata and header actions render
          // under the new bid's URL until this request returns — and the header's
          // delete/edit dialogs stay bound to the old bid object while they do.
          const isDifferentBid = get().selectedBid?.id !== id
          set({
            isLoadingBids: true,
            error: null,
            ...(isDifferentBid ? { selectedBid: null } : {}),
          })
          const bid = await api.get<Bid>(`/bids/${id}`)
          // Orphaned by a newer request or a reset. Without this, a slow fetch
          // for bid A landing after bid B's makes bid A the selected bid on bid
          // B's page — the "Analyse Bid" path does exactly that from a captured
          // closure, and every header action then targets the wrong bid.
          if (requestId !== selectedBidRequestId) return
          set({ selectedBid: bid, isLoadingBids: false })
        } catch (error) {
          if (requestId !== selectedBidRequestId) return
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch bid',
            isLoadingBids: false,
          })
        }
      },

      refreshBidDeadline: async (id: string) => {
        const { selectedBid, bids } = get()
        const selectedHeld = selectedBid?.id === id
        const listed = bids.find((b) => b.id === id)
        // Only a bid we hold can need patching.
        if (!selectedHeld && !listed) return
        // What each copy held before the request. A copy that changed while it
        // was in flight (the user saved a deadline) is newer than this read.
        // The time flag travels with the deadline (migration 183).
        const snapshot = (bid: DeadlineSnapshot): DeadlineSnapshot => ({
          deadline: bid.deadline,
          deadlineHasTime: bid.deadlineHasTime,
        })
        const selectedBefore = selectedHeld ? snapshot(selectedBid) : undefined
        const listedBefore = listed ? snapshot(listed) : undefined
        let server: DeadlineSnapshot
        try {
          server = snapshot(await api.get<Bid>(`/bids/${id}`))
        } catch {
          return
        }
        // Never clear locally: the server never removes a deadline on its own.
        const serverDeadline = server.deadline
        if (!serverDeadline) return
        // Patch only the deadline (and its flag), and only where it still holds
        // the value read before the request: an edit that landed meanwhile
        // always wins. When nothing moved, the state is left as is so nothing
        // re-renders.
        const serverHasTime = server.deadlineHasTime ?? null
        const needsPatch = (current: DeadlineSnapshot, before: DeadlineSnapshot | undefined) =>
          sameDeadline(current, before) && !sameDeadline(current, server)
        set((state) => {
          const patchList = state.bids.some((b) => b.id === id && needsPatch(b, listedBefore))
          const patchSelected =
            state.selectedBid?.id === id && needsPatch(state.selectedBid, selectedBefore)
          if (!patchList && !patchSelected) return state
          return {
            bids: patchList
              ? state.bids.map((b) =>
                  b.id === id && needsPatch(b, listedBefore)
                    ? { ...b, deadline: serverDeadline, deadlineHasTime: serverHasTime }
                    : b
                )
              : state.bids,
            selectedBid:
              patchSelected && state.selectedBid
                ? { ...state.selectedBid, deadline: serverDeadline, deadlineHasTime: serverHasTime }
                : state.selectedBid,
          }
        })
      },

      // Create new bid
      createBid: async (bidData: CreateBid) => {
        try {
          set({ isLoadingBids: true, error: null })
          const newBid = await api.post<Bid>('/bids', bidData)
          set((state) => ({
            bids: [...state.bids, newBid],
            isLoadingBids: false,
          }))
          return newBid
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to create bid',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Update existing bid
      updateBid: async (bidData: UpdateBid) => {
        try {
          set({ isLoadingBids: true, error: null })
          const updatedBid = await api.patch<Bid>(`/bids/${bidData.id}`, bidData)
          set((state) => ({
            bids: state.bids.map((b) => (b.id === updatedBid.id ? updatedBid : b)),
            selectedBid:
              state.selectedBid?.id === updatedBid.id ? updatedBid : state.selectedBid,
            isLoadingBids: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update bid',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Delete bid
      deleteBid: async (id: string) => {
        try {
          set({ isLoadingBids: true, error: null })
          await api.delete(`/bids/${id}`)
          set((state) => ({
            bids: state.bids.filter((b) => b.id !== id),
            selectedBid: state.selectedBid?.id === id ? null : state.selectedBid,
            isLoadingBids: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to delete bid',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Restricted/org visibility toggle (owner/admin only — server-gated).
      setBidVisibility: async (id: string, visibility: 'org' | 'restricted') => {
        try {
          const updated = await api.patch<Bid>(`/bids/${id}`, { _visibility: visibility })
          set((state) => ({
            bids: state.bids.map((b) => (b.id === id ? updated : b)),
            selectedBid: state.selectedBid?.id === id ? updated : state.selectedBid,
          }))
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to change visibility' })
          throw error
        }
      },

      // Route override (spec 24: the tier pill is gone; the route is set from
      // the edit dialog). No global loading flash — flipping isLoadingBids
      // would blank the bid list behind the open dialog.
      setBidScope: async (id: string, patch: { scopeProfile?: BidScopeProfile; bidRoute?: BidRoute }) => {
        try {
          const updated = await api.patch<Bid>(`/bids/${id}`, { _scope: patch })
          set((state) => ({
            bids: state.bids.map((b) => (b.id === id ? updated : b)),
            selectedBid: state.selectedBid?.id === id ? updated : state.selectedBid,
          }))
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to update bid scope' })
          throw error
        }
      },

      // Recently-deleted bids the caller may restore (30-day window).
      fetchDeletedBids: async () => {
        try {
          const result = await api.get<{ items: Bid[] }>('/bids?deleted=1')
          set({ deletedBids: result.items })
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to load deleted bids' })
          throw error
        }
      },

      // Restore a soft-deleted bid.
      restoreBid: async (id: string) => {
        try {
          await api.patch(`/bids/${id}`, { _restore: true })
          set((state) => ({ deletedBids: state.deletedBids.filter((b) => b.id !== id) }))
          await get().fetchBids()
        } catch (error) {
          set({ error: error instanceof Error ? error.message : 'Failed to restore bid' })
          throw error
        }
      },

      // Update bid status (separate endpoint for Kanban drag-drop)
      updateBidStatus: async (id: string, status: BidStatus) => {
        try {
          await api.patch(`/bids/${id}/status`, { status })
          set((state) => patchBidStatus(state, id, status))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update status',
          })
          throw error
        }
      },

      // Optimistic update for Kanban drag-drop (local state only)
      moveBid: (id: string, newStatus: BidStatus) => {
        set((state) => patchBidStatus(state, id, newStatus))
      },

      moveBidStage: async (id: string, stage: BidStage) => {
        const updated = await api.patch<Bid>(`/bids/${id}`, { stage })
        set((state) => replaceBid(state, updated))
      },

      moveBidLifecycle: async (id: string, move: LifecycleMove) => {
        const updated = await api.patch<Bid>(`/bids/${id}`, move)
        set((state) => replaceBid(state, updated))
      },

      refreshBid: async (id: string) => {
        try {
          const fresh = await api.get<Bid>(`/bids/${id}`)
          set((state) => replaceBid(state, fresh))
        } catch {
          // Best-effort: the next full load picks it up.
        }
      },

      // Select a bid for detail view
      selectBid: (bid: Bid | null) => {
        set({ selectedBid: bid })
      },

      // Set filter criteria
      setFilter: (filterUpdate: Partial<BidsFilter>) => {
        set((state) => ({
          filter: { ...state.filter, ...filterUpdate },
        }))
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Duplicate a bid
      duplicateBid: async (id: string) => {
        try {
          const bid = get().bids.find((b) => b.id === id)
          if (!bid) {
            throw new Error('Bid not found')
          }

          const duplicateData: CreateBid = {
            title: `${bid.title} (Copy)`,
            client: bid.client,
            // A copy starts where a new bid does: Active · Qualify. `status`
            // keeps a backend without the stage model in step.
            status: 'qualifying',
            lifecycle: 'active',
            stage: 'qualify',
            priority: bid.priority,
            value: bid.value,
            deadline: bid.deadline,
            // A copied deadline keeps its closing time (or stays date-only).
            ...(bid.deadline ? { deadlineHasTime: bid.deadlineHasTime === true } : {}),
            description: bid.description,
            organizationId: bid.organizationId,
            assignedTo: bid.assignedTo,
            tags: bid.tags,
          }

          return await get().createBid(duplicateData)
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to duplicate bid',
          })
          throw error
        }
      },

      // Archive bid
      archiveBid: async (id: string) => {
        try {
          set({ isLoadingBids: true, error: null })
          await api.patch(`/bids/${id}/archive`, {})
          set((state) => ({
            bids: state.bids.filter((b) => b.id !== id),
            selectedBid: state.selectedBid?.id === id ? null : state.selectedBid,
            isLoadingBids: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to archive bid',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Unarchive bid
      unarchiveBid: async (id: string) => {
        try {
          set({ isLoadingBids: true, error: null })
          const bid = await api.patch<Bid>(`/bids/${id}/unarchive`, {})
          set((state) => ({
            bids: [...state.bids, bid],
            isLoadingBids: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to unarchive bid',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Bulk update status
      bulkUpdateStatus: async (ids: string[], status: BidStatus) => {
        try {
          set({ isLoadingBids: true, error: null })
          await api.patch('/bids/bulk/status', { ids, status })
          set((state) => ({
            bids: state.bids.map((b) => (ids.includes(b.id) ? { ...b, status } : b)),
            isLoadingBids: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to bulk update status',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Bulk delete
      bulkDelete: async (ids: string[]) => {
        try {
          set({ isLoadingBids: true, error: null })
          await api.post('/bids/bulk/delete', { ids })
          set((state) => ({
            bids: state.bids.filter((b) => !ids.includes(b.id)),
            selectedBid:
              state.selectedBid && ids.includes(state.selectedBid.id)
                ? null
                : state.selectedBid,
            isLoadingBids: false,
          }))
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to bulk delete',
            isLoadingBids: false,
          })
          throw error
        }
      },

      // Fetch users
      fetchUsers: async () => {
        try {
          const users = await api.get<User[]>('/users')
          set({ users })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch users',
          })
        }
      },

      // Get filtered bids (for table/list view)
      getFilteredBids: () => {
        const { bids, filter } = get()
        return bids.filter((bid) => {
          // Filter by statuses
          if (filter.statuses?.length && !filter.statuses.includes(bid.status)) {
            return false
          }

          // R4: lifecycle tab and stage filter, on the derived fields so they
          // also hold against a backend that ignores the query params.
          if (filter.lifecycles?.length || filter.stages?.length) {
            const position = bidLifecycleOf(bid)
            if (filter.lifecycles?.length && !filter.lifecycles.includes(position.lifecycle)) {
              return false
            }
            if (filter.stages?.length && (!position.stage || !filter.stages.includes(position.stage))) {
              return false
            }
          }

          // Filter by priorities
          if (filter.priorities?.length && !filter.priorities.includes(bid.priority)) {
            return false
          }

          // Filter by clients
          if (filter.clients?.length && !filter.clients.includes(bid.client)) {
            return false
          }

          // Filter by search
          if (filter.search) {
            const search = filter.search.toLowerCase()
            if (
              !bid.title.toLowerCase().includes(search) &&
              !bid.client.toLowerCase().includes(search)
            ) {
              return false
            }
          }

          // Filter by deadline range
          // The filter's dates bound the viewer's own days, closing times included.
          if (filter.deadlineFrom && bid.deadline) {
            if (new Date(bid.deadline) < new Date(deadlineFilterBound(filter.deadlineFrom, 'from'))) {
              return false
            }
          }
          if (filter.deadlineTo && bid.deadline) {
            if (new Date(bid.deadline) > new Date(deadlineFilterBound(filter.deadlineTo, 'to'))) {
              return false
            }
          }

          // Filter by value range
          if (filter.valueMin !== undefined && bid.value !== undefined) {
            if (bid.value < filter.valueMin) {
              return false
            }
          }
          if (filter.valueMax !== undefined && bid.value !== undefined) {
            if (bid.value > filter.valueMax) {
              return false
            }
          }

          return true
        })
      },
    }),
    { name: 'bids-store' }
  )
)
